// Mobile-compatible audio utility for cross-platform audio playback
// Handles iOS, Android, and all mobile browsers with proper audio context management

export interface AudioConfig {
  preload?: boolean;
  volume?: number;
  loop?: boolean;
  crossOrigin?: string;
  fallbackText?: string;
}

export interface AudioPlaybackResult {
  success: boolean;
  method: 'audio' | 'web_audio' | 'tts' | 'failed';
  error?: string;
}

class MobileAudioManager {
  private audioContext: AudioContext | null = null;
  private audioCache = new Map<string, HTMLAudioElement>();
  private audioBufferCache = new Map<string, AudioBuffer>();
  private isInitialized = false;
  private isMobile = false;
  private isIOS = false;
  private isAndroid = false;
  private userInteracted = false;
  private speechSynthesis: SpeechSynthesis | null = null;

  constructor() {
    this.detectPlatform();
    this.setupEventListeners();
    this.initializeSpeechSynthesis();
  }

  private detectPlatform(): void {
    const userAgent = navigator.userAgent.toLowerCase();
    this.isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    this.isIOS = /iphone|ipad|ipod/i.test(userAgent);
    this.isAndroid = /android/i.test(userAgent);
    
    console.log('🔍 Platform detected:', { 
      isMobile: this.isMobile, 
      isIOS: this.isIOS, 
      isAndroid: this.isAndroid 
    });
  }

  private setupEventListeners(): void {
    // Listen for user interaction to enable audio context
    const events = ['touchstart', 'click', 'keydown', 'pointerdown'];
    
    const handleUserInteraction = () => {
      if (!this.userInteracted) {
        this.userInteracted = true;
        this.initializeAudioContext();
        console.log('✅ User interaction detected, audio context enabled');
        
        // Remove listeners after first interaction
        events.forEach(event => {
          document.removeEventListener(event, handleUserInteraction);
        });
      }
    };

    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { once: true, passive: true });
    });
  }

  private async initializeAudioContext(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Create audio context with proper vendor prefixes
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      
      if (AudioContextClass) {
        this.audioContext = new AudioContextClass();
        
        // Resume context if suspended (required for iOS)
        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
        }
        
        console.log('✅ Audio context initialized:', this.audioContext.state);
      }
      
      this.isInitialized = true;
    } catch (error) {
      console.warn('⚠️ Failed to initialize audio context:', error);
    }
  }

  private initializeSpeechSynthesis(): void {
    if ('speechSynthesis' in window) {
      this.speechSynthesis = window.speechSynthesis;
      console.log('✅ Speech synthesis available');
    } else {
      console.warn('⚠️ Speech synthesis not available');
    }
  }

  private createMobileAudioElement(url: string, config: AudioConfig = {}): HTMLAudioElement {
    const audio = new Audio();
    
    // Configure for mobile compatibility
    audio.crossOrigin = config.crossOrigin || 'anonymous';
    audio.preload = config.preload ? 'auto' : 'metadata';
    audio.volume = config.volume ?? 1.0;
    audio.loop = config.loop ?? false;
    
    // iOS-specific attributes
    if (this.isIOS) {
      audio.setAttribute('webkit-playsinline', 'true');
      audio.setAttribute('playsinline', 'true');
    }
    
    // Android-specific optimizations
    if (this.isAndroid) {
      audio.setAttribute('preload', 'metadata');
    }
    
    // Set source after configuration
    audio.src = url;
    
    return audio;
  }

  private async loadAudioBuffer(url: string): Promise<AudioBuffer | null> {
    if (!this.audioContext) return null;
    
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      
      this.audioBufferCache.set(url, audioBuffer);
      console.log('✅ Audio buffer loaded:', url);
      
      return audioBuffer;
    } catch (error) {
      console.error('❌ Failed to load audio buffer:', error);
      return null;
    }
  }

  private async playWithWebAudio(audioBuffer: AudioBuffer): Promise<void> {
    if (!this.audioContext) {
      console.log('🚫 Audio context not available');
      return;
    }
    
    const source = this.audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.audioContext.destination);
    source.start(0);
    
    return new Promise((resolve, reject) => {
      source.onended = () => resolve();
      // AudioBufferSourceNode doesn't have onerror, use try-catch for error handling
      try {
        // The promise will resolve when the audio ends
      } catch (error) {
        reject(error);
      }
    });
  }

  private async playWithHTMLAudio(audio: HTMLAudioElement): Promise<void> {
    // For mobile, especially iOS, we need to handle play() promises properly
    try {
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        await playPromise;
      }
    } catch (error) {
      // Handle autoplay policy violations
      if (error.name === 'NotAllowedError') {
        console.log('🚫 Audio autoplay blocked. User interaction required.');
        return;
      }
      console.log('🚫 Audio playback error:', error);
      return;
    }
  }

  private async playWithTTS(text: string): Promise<void> {
    if (!this.speechSynthesis || !text) {
      console.log('🚫 Speech synthesis not available or no text provided - TTS functionality removed');
      return;
    }

    return new Promise((resolve, reject) => {
      // Cancel any ongoing speech
      this.speechSynthesis!.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      
      // Try to find a Persian voice
      const voices = this.speechSynthesis!.getVoices();
      const persianVoice = voices.find(voice => 
        voice.lang.includes('fa') || 
        voice.lang.includes('persian') ||
        voice.name.toLowerCase().includes('persian')
      );
      
      if (persianVoice) {
        utterance.voice = persianVoice;
        utterance.lang = 'fa-IR';
      } else {
        // Fallback to default voice with Persian language code
        utterance.lang = 'fa-IR';
      }
      
      utterance.rate = 0.8;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      
      utterance.onend = () => resolve();
      utterance.onerror = (error) => reject(error);
      
      this.speechSynthesis!.speak(utterance);
    });
  }

  /**
   * Preload audio for better mobile performance
   */
  async preloadAudio(url: string, config: AudioConfig = {}): Promise<boolean> {
    try {
      // Ensure user has interacted first
      if (!this.userInteracted) {
        console.log('⏳ Waiting for user interaction to preload audio');
        return false;
      }

      await this.initializeAudioContext();

      // Create and cache audio element
      const audio = this.createMobileAudioElement(url, { ...config, preload: true });
      this.audioCache.set(url, audio);

      // Also try to load as audio buffer for Web Audio API
      if (this.audioContext) {
        await this.loadAudioBuffer(url);
      }

      console.log('✅ Audio preloaded:', url);
      return true;
    } catch (error) {
      console.warn('⚠️ Failed to preload audio:', error);
      return false;
    }
  }

  /**
   * Play audio with mobile compatibility and fallbacks
   */
  async playAudio(url: string, config: AudioConfig = {}): Promise<AudioPlaybackResult> {
    // Ensure user has interacted first
    if (!this.userInteracted) {
      return {
        success: false,
        method: 'failed',
        error: 'User interaction required for audio playback'
      };
    }

    await this.initializeAudioContext();

    // Method 1: Try Web Audio API (best for mobile)
    if (this.audioContext && this.audioBufferCache.has(url)) {
      try {
        const audioBuffer = this.audioBufferCache.get(url)!;
        await this.playWithWebAudio(audioBuffer);
        return { success: true, method: 'web_audio' };
      } catch (error) {
        console.warn('⚠️ Web Audio API failed, trying HTML Audio:', error);
      }
    }

    // Method 2: Try HTML Audio Element
    try {
      let audio = this.audioCache.get(url);
      
      if (!audio) {
        audio = this.createMobileAudioElement(url, config);
        this.audioCache.set(url, audio);
      }

      // Reset audio to beginning
      audio.currentTime = 0;
      
      await this.playWithHTMLAudio(audio);
      return { success: true, method: 'audio' };
    } catch (error) {
      console.warn('⚠️ HTML Audio failed:', error);
    }

    // Method 3: Fallback to TTS
    if (config.fallbackText) {
      try {
        await this.playWithTTS(config.fallbackText);
        return { success: true, method: 'tts' };
      } catch (error) {
        console.error('❌ TTS fallback failed:', error);
      }
    }

    return {
      success: false,
      method: 'failed',
      error: 'All audio playback methods failed'
    };
  }

  /**
   * Initialize audio system with user gesture (call this on first user interaction)
   */
  async initializeWithUserGesture(): Promise<boolean> {
    try {
      this.userInteracted = true;
      await this.initializeAudioContext();
      
      // Test audio capability with a short silent audio
      if (this.audioContext) {
        const buffer = this.audioContext.createBuffer(1, 1, 22050);
        const source = this.audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.audioContext.destination);
        source.start(0);
      }
      
      console.log('✅ Audio system initialized with user gesture');
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize audio system:', error);
      return false;
    }
  }

  /**
   * Clear audio cache to free memory
   */
  clearCache(): void {
    this.audioCache.clear();
    this.audioBufferCache.clear();
    console.log('🗑️ Audio cache cleared');
  }

  /**
   * Get system information
   */
  getSystemInfo(): object {
    return {
      isMobile: this.isMobile,
      isIOS: this.isIOS,
      isAndroid: this.isAndroid,
      userInteracted: this.userInteracted,
      audioContextState: this.audioContext?.state,
      speechSynthesisAvailable: !!this.speechSynthesis,
      cacheSize: {
        audioElements: this.audioCache.size,
        audioBuffers: this.audioBufferCache.size
      }
    };
  }
}

// Create singleton instance
const mobileAudioManager = new MobileAudioManager();

// Export convenience functions
export const playMobileAudio = (url: string, config?: AudioConfig): Promise<AudioPlaybackResult> => {
  return mobileAudioManager.playAudio(url, config);
};

export const preloadMobileAudio = (url: string, config?: AudioConfig): Promise<boolean> => {
  return mobileAudioManager.preloadAudio(url, config);
};

export const initializeAudioSystem = (): Promise<boolean> => {
  return mobileAudioManager.initializeWithUserGesture();
};

export const clearAudioCache = (): void => {
  mobileAudioManager.clearCache();
};

export const getAudioSystemInfo = (): object => {
  return mobileAudioManager.getSystemInfo();
};

export default mobileAudioManager; 